import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  addMonths,
  buildMonthGrid,
  eventDisplayTitle,
  formatDateLabel,
  formatMonthTitle,
  getNowCreateSlot,
  startOfMonth,
  timeToMinutes,
  toIsoDateLocal,
} from './constants';
import {
  apiEventToMonthEvent,
  monthEventToApiPayload,
  expandEventToDaySegments,
  isEndAfterStart,
  addDaysToIso,
} from '../planning/eventApiMappers';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../../services/api';
import MonthDayCell from './MonthDayCell';
import MoreEventsPopover from './MoreEventsPopover';
import EventModal from './EventModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import './monthView.css';

let idCounter = 0;
function nextId() {
  idCounter += 1;
  return `mev-${Date.now()}-${idCounter}`;
}

function isPersistedId(id) {
  return typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id));
}

function sameEventId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

const ALL_DAY_START = '00:00';
const ALL_DAY_END = '23:59';
const DEFAULT_TIMED_START = '09:00';
const DEFAULT_TIMED_END = '10:00';

function isMonthAllDay(start, end) {
  const s = String(start || '');
  const e = String(end || '');
  return s === ALL_DAY_START && (e === ALL_DAY_END || timeToMinutes(e) >= 23 * 60 + 59);
}

const monthSlideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 64 : -64,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -64 : 64,
    opacity: 0,
  }),
};

const EmployeeMonthViewPage = forwardRef(function EmployeeMonthViewPage(
  { onMonthRangeChange, userId = null, readOnly = false },
  ref
) {
  const { t } = useTranslation('planning');
  const [monthDate, setMonthDate] = useState(() => startOfMonth());
  const [monthDirection, setMonthDirection] = useState(0);
  const monthTitle = useMemo(() => formatMonthTitle(monthDate), [monthDate]);
  const monthKey = monthDate.getTime();
  const { headers, cells } = useMemo(() => buildMonthGrid(monthDate), [monthDate]);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [modal, setModal] = useState(null);
  const [timeError, setTimeError] = useState(false);

  const [morePopover, setMorePopover] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [dragEventId, setDragEventId] = useState(null);
  const [dragFromMore, setDragFromMore] = useState(false);
  const [dragOverIso, setDragOverIso] = useState(null);
  const suppressClickRef = useRef(false);
  const dragEventIdRef = useRef(null);
  const dragSourceIsoRef = useRef(null);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const monthTitleRef = useRef(monthTitle);
  monthTitleRef.current = monthTitle;

  useEffect(() => {
    onMonthRangeChange?.(monthTitle);
  }, [monthTitle, onMonthRangeChange]);

  useEffect(() => {
    let cancelled = false;
    const dateFrom = cells[0]?.iso;
    const dateTo = cells[cells.length - 1]?.iso;
    if (!dateFrom || !dateTo) return undefined;

    (async () => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const filters = { date_from: dateFrom, date_to: dateTo };
        if (userId != null) filters.employe = userId;
        const data = await getEvents(filters);
        const list = Array.isArray(data) ? data : data?.results || [];
        if (cancelled) return;
        setEvents(list.map(apiEventToMonthEvent));
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load events', err);
        setEventsError(err?.message || t('failedToLoadEvents'));
        setEvents([]);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [monthKey, cells, userId]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) => {
      const title = eventDisplayTitle(ev).toLowerCase();
      const cat = String(ev.category || '').toLowerCase();
      return title.includes(q) || cat.includes(q);
    });
  }, [events, searchQuery]);

  const eventsByDate = useMemo(() => {
    const map = {};
    filteredEvents.forEach((ev) => {
      const segs = expandEventToDaySegments(ev, {
        useMinutes: false,
        startKey: 'start',
        endKey: 'end',
        dateKey: 'date',
        endDateKey: 'endDate',
      });
      segs.forEach((seg) => {
        if (!map[seg.date]) map[seg.date] = [];
        map[seg.date].push(seg);
      });
    });
    Object.keys(map).forEach((iso) => {
      map[iso].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
    });
    return map;
  }, [filteredEvents]);

  const closeMore = useCallback(() => setMorePopover(null), []);
  const closeModal = useCallback(() => {
    setModal(null);
    setTimeError(false);
  }, []);
  const closeDelete = useCallback(() => setDeleteConfirm(null), []);

  const openCreate = useCallback((date, start = '09:00', end = '10:00') => {
    if (readOnly) return;
    closeMore();
    setTimeError(false);
    setModal({
      mode: 'create',
      eventId: null,
      title: '',
      date,
      endDate: date,
      start,
      end,
      allDay: false,
      category: 'HO',
    });
  }, [closeMore, readOnly]);

  const openEdit = useCallback((event) => {
    if (readOnly) return;
    closeMore();
    setTimeError(false);
    const full = eventsRef.current.find((ev) => ev.id === event.id) || event;
    setModal({
      mode: 'edit',
      eventId: full.id,
      title: full.title || '',
      date: full.date,
      endDate: full.endDate || full.date,
      start: full.start,
      end: full.end,
      allDay: isMonthAllDay(full.start, full.end),
      category: full.category || 'HO',
    });
  }, [closeMore, readOnly]);

  const openCreateForToday = useCallback((withNowSlot = false) => {
    if (readOnly) return;
    const todayIso = toIsoDateLocal(new Date());
    const slot = withNowSlot ? getNowCreateSlot() : { start: '09:00', end: '10:00' };
    const today = startOfMonth(new Date());
    const prev = monthDate;
    const delta = today.getTime() - prev.getTime();
    if (delta !== 0) {
      setMonthDirection(delta > 0 ? 1 : -1);
      setMonthDate(today);
    }
    closeMore();
    openCreate(todayIso, slot.start, slot.end);
  }, [openCreate, closeMore, monthDate, readOnly]);

  const goPrevMonth = useCallback(() => {
    closeMore();
    setMonthDirection(-1);
    setMonthDate((d) => addMonths(d, -1));
  }, [closeMore]);

  const goNextMonth = useCallback(() => {
    closeMore();
    setMonthDirection(1);
    setMonthDate((d) => addMonths(d, 1));
  }, [closeMore]);

  const goToday = useCallback(() => {
    const today = startOfMonth(new Date());
    const delta = today.getTime() - monthDate.getTime();
    if (delta === 0) return;
    closeMore();
    setMonthDirection(delta > 0 ? 1 : -1);
    setMonthDate(today);
  }, [closeMore, monthDate]);

  useImperativeHandle(
    ref,
    () => ({
      goPrevMonth,
      goNextMonth,
      goToday,
      openCreateEvent: () => openCreateForToday(false),
      setSearchQuery,
      getMonthLabel: () => monthTitleRef.current,
    }),
    [goPrevMonth, goNextMonth, goToday, openCreateForToday]
  );

  const handleModalChange = useCallback((patch) => {
    setModal((prev) => {
      if (!prev) return prev;
      if (Object.prototype.hasOwnProperty.call(patch, 'allDay')) {
        if (patch.allDay) {
          return {
            ...prev,
            allDay: true,
            prevStart: prev.start,
            prevEnd: prev.end,
            start: ALL_DAY_START,
            end: ALL_DAY_END,
          };
        }
        return {
          ...prev,
          allDay: false,
          start: prev.prevStart || DEFAULT_TIMED_START,
          end: prev.prevEnd || DEFAULT_TIMED_END,
        };
      }
      const next = { ...prev, ...patch };
      if (patch.date && (!next.endDate || next.endDate < patch.date)) {
        next.endDate = patch.date;
      }
      if (patch.start != null || patch.end != null) {
        next.allDay = isMonthAllDay(next.start, next.end);
      }
      return next;
    });
    if (
      patch.start != null ||
      patch.end != null ||
      patch.date != null ||
      patch.endDate != null ||
      Object.prototype.hasOwnProperty.call(patch, 'allDay')
    ) {
      setTimeError(false);
    }
  }, []);

  const handleModalSubmit = useCallback(async () => {
    if (!modal) return;
    const endDate = modal.endDate || modal.date;
    if (!isEndAfterStart(modal.date, modal.start, endDate, modal.end)) {
      setTimeError(true);
      return;
    }
    const payload = {
      title: String(modal.title || '').trim(),
      date: modal.date,
      endDate,
      start: modal.start,
      end: modal.end,
      category: modal.category || 'HO',
    };
    if (modal.mode === 'edit' && modal.eventId) {
      const prev = eventsRef.current.find((ev) => ev.id === modal.eventId);
      setEvents((list) =>
        list.map((ev) => (ev.id === modal.eventId ? { ...ev, ...payload } : ev))
      );
      closeModal();
      if (!isPersistedId(modal.eventId)) return;
      try {
        const updated = await updateEvent(modal.eventId, monthEventToApiPayload(payload));
        setEvents((list) =>
          list.map((ev) => (ev.id === modal.eventId ? apiEventToMonthEvent(updated) : ev))
        );
      } catch (err) {
        console.error('Failed to update event', err);
        if (prev) {
          setEvents((list) => list.map((ev) => (ev.id === modal.eventId ? prev : ev)));
        }
        setEventsError(err?.message || t('failedToUpdateEvent'));
      }
    } else {
      const tempId = nextId();
      setEvents((list) => [...list, { id: tempId, ...payload }]);
      closeModal();
      try {
        const created = await createEvent(monthEventToApiPayload(payload));
        setEvents((list) =>
          list.map((ev) => (ev.id === tempId ? apiEventToMonthEvent(created) : ev))
        );
      } catch (err) {
        console.error('Failed to create event', err);
        setEvents((list) => list.filter((ev) => ev.id !== tempId));
        setEventsError(err?.message || t('failedToCreateEvent'));
      }
    }
  }, [modal, closeModal]);

  const requestDelete = useCallback((event, clientX, clientY) => {
    if (readOnly) return;
    setDeleteConfirm({
      event,
      cursorX: clientX,
      cursorY: clientY,
    });
  }, [readOnly]);

  const confirmDelete = useCallback(async () => {
    if (readOnly) return;
    if (!deleteConfirm?.event) return;
    const id = deleteConfirm.event.id;
    const removed = deleteConfirm.event;
    setEvents((prev) => prev.filter((ev) => ev.id !== id));
    setDeleteConfirm(null);
    if (modal?.eventId === id) closeModal();
    if (morePopover?.dateIso === deleteConfirm.event.date) {
      setMorePopover((prev) => {
        if (!prev) return null;
        const nextEvents = (eventsByDate[prev.dateIso] || []).filter((e) => e.id !== id);
        if (nextEvents.length === 0) return null;
        return { ...prev };
      });
    }
    if (!isPersistedId(id)) return;
    try {
      await deleteEvent(id);
    } catch (err) {
      console.error('Failed to delete event', err);
      setEvents((prev) => [...prev, removed]);
      setEventsError(err?.message || t('failedToDeleteEvent'));
    }
  }, [deleteConfirm, modal, closeModal, morePopover, eventsByDate, readOnly]);

  const handleDeleteFromModal = useCallback(() => {
    if (!modal?.eventId) return;
    const event = events.find((e) => e.id === modal.eventId);
    if (!event) return;
    const rect = document.getElementById('month-event-title')?.getBoundingClientRect();
    requestDelete(
      event,
      rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
      rect ? rect.bottom + 8 : window.innerHeight / 2
    );
  }, [modal, events, requestDelete]);

  const handleEventDragStart = useCallback((event, e, fromMore) => {
    if (readOnly) {
      e.preventDefault();
      return;
    }
    dragEventIdRef.current = event.id;
    dragSourceIsoRef.current = event.date;
    setDragEventId(event.id);
    setDragFromMore(!!fromMore);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(event.id));
    try {
      e.dataTransfer.setData('application/x-month-event-id', String(event.id));
    } catch (_) {
      /* ignore */
    }
  }, [readOnly]);

  const handleEventDragEnd = useCallback(() => {
    dragEventIdRef.current = null;
    dragSourceIsoRef.current = null;
    setDragEventId(null);
    setDragFromMore(false);
    setDragOverIso(null);
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 100);
  }, []);

  const moveEventToDate = useCallback(async (eventId, targetIso) => {
    if (readOnly) return;
    if (!eventId || !targetIso) return;
    const prev = eventsRef.current.find((ev) => sameEventId(ev.id, eventId));
    if (!prev) {
      setMorePopover(null);
      return;
    }
    const sourceIso = dragSourceIsoRef.current || prev.date;
    if (sourceIso === targetIso) {
      setMorePopover(null);
      return;
    }
    const dayDelta = Math.round(
      (Date.parse(`${targetIso}T00:00:00Z`) - Date.parse(`${sourceIso}T00:00:00Z`)) / 86400000
    );
    const nextDate = addDaysToIso(prev.date, dayDelta);
    const nextEndDate = addDaysToIso(prev.endDate || prev.date, dayDelta);
    const next = { ...prev, date: nextDate, endDate: nextEndDate };
    setEvents((list) =>
      list.map((ev) => (sameEventId(ev.id, eventId) ? next : ev))
    );
    setMorePopover(null);
    if (!isPersistedId(prev.id)) return;
    try {
      await updateEvent(prev.id, monthEventToApiPayload(next));
    } catch (err) {
      console.error('Failed to move event', err);
      setEvents((list) =>
        list.map((ev) => (sameEventId(ev.id, eventId) ? prev : ev))
      );
      setEventsError(err?.message || t('failedToMoveEvent'));
    }
  }, [readOnly]);

  const handleDayDragOver = useCallback((e, cell) => {
    if (readOnly) return;
    if (!cell?.iso) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIso(cell.iso);
  }, [readOnly]);

  const handleDayDragLeave = useCallback((e, cell) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIso((prev) => (prev === cell.iso ? null : prev));
    }
  }, []);

  const handleDayDrop = useCallback((e, cell) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    if (!cell?.iso) return;
    const fromTransfer =
      e.dataTransfer.getData('application/x-month-event-id') ||
      e.dataTransfer.getData('text/plain');
    // Prefer in-memory id (preserves number type from API); dataTransfer is always a string
    const id = dragEventIdRef.current ?? dragEventId ?? fromTransfer;
    if (id != null && id !== '') {
      moveEventToDate(id, cell.iso);
    }
    dragEventIdRef.current = null;
    dragSourceIsoRef.current = null;
    setDragOverIso(null);
    setDragEventId(null);
    setDragFromMore(false);
  }, [dragEventId, moveEventToDate, readOnly]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (deleteConfirm) {
          closeDelete();
          return;
        }
        if (modal) {
          closeModal();
          return;
        }
        if (morePopover) {
          closeMore();
        }
        return;
      }
      if (readOnly) return;
      if (e.key === 'N' && e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
        e.preventDefault();
        openCreateForToday(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deleteConfirm, modal, morePopover, closeDelete, closeModal, closeMore, openCreateForToday, readOnly]);

  useEffect(() => {
    if (!morePopover) return undefined;
    const onPointer = (e) => {
      const panel = document.querySelector('.month-more-popover');
      if (panel && panel.contains(e.target)) return;
      if (e.target.closest?.('.month-more')) return;
      closeMore();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointer);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [morePopover, closeMore]);

  const deleteWhenLabel = deleteConfirm?.event
    ? `${formatDateLabel(deleteConfirm.event.date)} · ${deleteConfirm.event.start} – ${deleteConfirm.event.end}`
    : '';

  return (
    <div
      className={`month-view-root${dragEventId ? ' is-dragging-event' : ''}${readOnly ? ' is-readonly' : ''}`}
    >
      {eventsLoading && (
        <div className="absolute inset-x-0 top-0 z-20 px-4 py-2 text-xs text-text-secondary bg-surface/90 border-b border-border">
          Loading events…
        </div>
      )}
      {eventsError && (
        <div className="absolute inset-x-0 top-0 z-20 px-4 py-2 text-xs text-rose-600 bg-rose-50 border-b border-rose-100 flex items-center justify-between gap-2">
          <span>{eventsError}</span>
          <button type="button" className="shrink-0 underline" onClick={() => setEventsError(null)}>
            Dismiss
          </button>
        </div>
      )}
      <div className="flex-1 overflow-hidden p-4 sm:p-6 min-h-0 flex flex-col">
        <div className="border border-border rounded-xl overflow-hidden bg-surface shadow-card flex-1 min-h-0 flex flex-col">
          <div className="grid grid-cols-7 border-b border-border bg-background-light shrink-0">
            {headers.map((h, i) => (
              <div
                key={h}
                className={`py-3 text-center text-xs font-medium text-text-secondary${
                  i < 6 ? ' border-r border-border' : ''
                }`}
              >
                {h}
              </div>
            ))}
          </div>
          <div className="relative flex-1 min-h-0 overflow-hidden">
            <AnimatePresence initial={false} mode="wait" custom={monthDirection}>
              <motion.div
                key={monthKey}
                custom={monthDirection}
                variants={monthSlideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0 grid grid-cols-7 grid-rows-6 overflow-hidden"
              >
                {cells.map((cell, idx) => {
                  const col = idx % 7;
                  const row = Math.floor(idx / 7);
                  const dayEvents = eventsByDate[cell.iso] || [];
                  return (
                    <MonthDayCell
                      key={cell.iso}
                      cell={cell}
                      events={dayEvents}
                      isLastCol={col === 6}
                      isLastRow={row === 5}
                      dragOver={dragOverIso === cell.iso}
                      draggingEventId={dragEventId}
                      readOnly={readOnly}
                      onDayClick={(c) => openCreate(c.iso)}
                      onEventClick={(ev) => {
                        if (suppressClickRef.current) return;
                        openEdit(ev);
                      }}
                      onEventContextMenu={requestDelete}
                      onEventDragStart={handleEventDragStart}
                      onEventDragEnd={handleEventDragEnd}
                      onMoreClick={(c, anchorEl) => {
                        const rect = anchorEl.getBoundingClientRect();
                        setMorePopover({
                          dateIso: c.iso,
                          anchorRect: {
                            left: rect.left,
                            top: rect.top,
                            bottom: rect.bottom,
                            right: rect.right,
                          },
                        });
                      }}
                      onDragOver={(e) => handleDayDragOver(e, cell)}
                      onDragLeave={(e) => handleDayDragLeave(e, cell)}
                      onDrop={(e) => handleDayDrop(e, cell)}
                    />
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      <MoreEventsPopover
        open={!!morePopover}
        dateIso={morePopover?.dateIso}
        events={morePopover ? eventsByDate[morePopover.dateIso] || [] : []}
        anchorRect={morePopover?.anchorRect}
        draggingAway={dragFromMore && !!dragEventId}
        draggingEventId={dragEventId}
        onClose={closeMore}
        onEventClick={(ev) => {
          if (suppressClickRef.current) return;
          openEdit(ev);
        }}
        onEventContextMenu={requestDelete}
        onEventDragStart={handleEventDragStart}
        onEventDragEnd={handleEventDragEnd}
      />

      {!readOnly && (
        <>
          <EventModal
            open={!!modal}
            mode={modal?.mode || 'create'}
            title={modal?.title || ''}
            date={modal?.date || ''}
            endDate={modal?.endDate || modal?.date || ''}
            start={modal?.start || '09:00'}
            end={modal?.end || '10:00'}
            allDay={!!modal?.allDay}
            category={modal?.category || 'HO'}
            timeError={timeError}
            onChange={handleModalChange}
            onClose={closeModal}
            onSubmit={handleModalSubmit}
            onDelete={handleDeleteFromModal}
          />

          <DeleteConfirmModal
            open={!!deleteConfirm}
            cursorX={deleteConfirm?.cursorX || 0}
            cursorY={deleteConfirm?.cursorY || 0}
            title={deleteConfirm ? eventDisplayTitle(deleteConfirm.event) : ''}
            whenLabel={deleteWhenLabel}
            onCancel={closeDelete}
            onConfirm={confirmDelete}
          />
        </>
      )}
    </div>
  );
});

export default EmployeeMonthViewPage;
